import {idFromGlobalId} from '../../../../forecast-app/shared/util/GlobalIdUtil';
import {encodeCompanyId} from '../featureFlagRows';

/** Company shape the rollout modal needs — a structural subset of the Relay company list. */
export interface DirectoryCompany {
	readonly companyId: number | null;
	readonly name: string | null;
}

/**
 * Person shape the rollout modal needs — a structural subset of the Relay person lists.
 *
 * `companyId` is an encoded company global id where the backend supplies one. The Forecast person
 * list carries no company field at all, so entries from it arrive without the property.
 */
export interface DirectoryPerson {
	readonly id: string | null;
	readonly firstName: string | null;
	readonly lastName: string | null;
	readonly email: string | null;
	readonly companyId?: string | null;
}

export interface RolloutPersonOption {
	/** Encoded `Person` global id — what the mutation grants against. */
	id: string;
	/** `Name (email)`, degrading to whichever of the two the backend has. */
	name: string;
}

export interface RolloutCandidate {
	/** Encoded `CompanyType` global id. */
	companyId: string;
	companyName: string;
	/** Active, non-virtual, non-client people at the company — the rollout's blast radius. */
	userCount: number;
}

export interface RolloutDirectory {
	/** Encoded company id to company name. */
	companyNames: Map<string, string>;
	/** Encoded company id to the people who could be granted the flag individually. */
	personsByCompanyId: Map<string, RolloutPersonOption[]>;
}

const isPresent = <T>(value: T | null | undefined): value is T => value != null;

/** The company id Forecast's own people belong to. Their person records carry no company field. */
const FORECAST_COMPANY_ID = encodeCompanyId(1);

/** `Name (email)`, degrading to whichever of the two the backend recorded. */
function personLabel(person: DirectoryPerson): string {
	const name = [person.firstName, person.lastName].filter(part => part).join(' ');
	const email = person.email ?? '';
	if (name && email) {
		return `${name} (${email})`;
	}
	return name || email;
}

/**
 * Something identifying to call a company the company list has no name for, matching how the
 * assignment table names one. The numeric id is what an admin can actually look up.
 */
function fallbackCompanyName(encodedCompanyId: string): string {
	const numericId = idFromGlobalId(encodedCompanyId);
	return `Company ${Number.isNaN(numericId) ? encodedCompanyId : numericId}`;
}

/**
 * The company names and per-company people the rollout modal works from.
 *
 * Everything is keyed by encoded company id so it can be matched against a flag's grants without
 * converting at each use. The company list arrives with raw numeric ids and is encoded here; the
 * person lists already carry encoded ids.
 *
 * The two person lists together are the backend's impersonation-eligible population — active,
 * non-virtual, non-client, non-system people — which is the right population to measure a rollout's
 * blast radius in, and the reason per-company user counts are derived here rather than requested as a
 * field of their own.
 */
export function buildRolloutDirectory(
	companies: ReadonlyArray<DirectoryCompany | null> | null | undefined,
	otherPersons: ReadonlyArray<DirectoryPerson | null> | null | undefined,
	forecastPersons: ReadonlyArray<DirectoryPerson | null> | null | undefined
): RolloutDirectory {
	const companyNames = new Map<string, string>();
	(companies ?? []).filter(isPresent).forEach(company => {
		if (company.companyId == null) {
			return;
		}
		companyNames.set(encodeCompanyId(company.companyId), company.name ?? '');
	});

	const personsByCompanyId = new Map<string, RolloutPersonOption[]>();
	const addPerson = (person: DirectoryPerson, companyId: string): void => {
		if (person.id == null) {
			return;
		}
		const option = {id: person.id, name: personLabel(person)};
		const existing = personsByCompanyId.get(companyId);
		if (existing) {
			existing.push(option);
		} else {
			personsByCompanyId.set(companyId, [option]);
		}
	};

	(otherPersons ?? []).filter(isPresent).forEach(person => {
		if (person.companyId == null) {
			return;
		}
		addPerson(person, person.companyId);
	});
	(forecastPersons ?? []).filter(isPresent).forEach(person => addPerson(person, FORECAST_COMPANY_ID));

	return {companyNames, personsByCompanyId};
}

/**
 * Preview rows for a list of companies, in the order given — which for a percentage rollout is the
 * fill order, and must not be re-sorted here.
 */
export function buildRolloutCandidates(companyIds: readonly string[], directory: RolloutDirectory): RolloutCandidate[] {
	return companyIds.map(companyId => ({
		companyId,
		companyName: directory.companyNames.get(companyId) || fallbackCompanyName(companyId),
		userCount: directory.personsByCompanyId.get(companyId)?.length ?? 0,
	}));
}

/**
 * Every company, sorted by name, with the eligible ones marked.
 *
 * All companies are listed rather than only eligible ones: granting a flag to a trial company
 * deliberately is a legitimate internal action. It simply does not move the rollout percentage, which
 * is what `active` tells the reader.
 *
 * `name` stays the raw company name — it is also what the company picker's ComboBox filters on, and
 * baking an " (active)" suffix into it would make every eligible company match a search for "active"
 * regardless of its actual name. `active` is a separate field so a caller can render the marker
 * without it becoming part of the searchable or displayed name text.
 */
export function companyOptionsForPicker(
	directory: RolloutDirectory,
	eligibleCompanyIds: Set<string>
): Array<{id: string; name: string; active: boolean}> {
	return Array.from(directory.companyNames.entries())
		.map(([companyId, name]) => ({
			id: companyId,
			name: name || fallbackCompanyName(companyId),
			active: eligibleCompanyIds.has(companyId),
		}))
		.sort((a, b) => a.name.localeCompare(b.name));
}
